import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Github, ArrowRight, CheckCircle2, Terminal, Cpu, Database, BookOpen, Layers } from "lucide-react";
import TechTag from "../common/TechTag";

export default function SupportingProjectCard({ project, index = 0 }) {
  const { id, name, type, desc, stack = [], liveUrl, repoUrl, slug } = project;
  const projectSlug = slug || name.toLowerCase().replace(/\s+/g, "-");

  // Determine specific status, icon, and preview contents based on project
  let statusBadge = "Verified Production";
  let statusColor = "border-emerald-500/40 text-emerald-400 bg-emerald-500/10";
  let previewHeader = "mavecode.my.id // curriculum";

  if (name === "MaveMentor AI") {
    statusBadge = "Prototype & Evaluated";
    statusColor = "border-amber-500/40 text-amber-300 bg-amber-500/10";
    previewHeader = "api.mavementor.internal // rag";
  } else if (name === "AI Content Curation") {
    statusBadge = "Academic Research Tool";
    statusColor = "border-violet-500/40 text-violet-300 bg-violet-500/10";
    previewHeader = "research.slr // prisma-extract";
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group rounded-2xl border border-border/80 bg-gradient-to-b from-card/75 via-card/45 to-[#07070b] overflow-hidden shadow-xl hover:border-primary/50 transition-all duration-300 flex flex-col justify-between"
    >
      <div>
        {/* Visual Product/System Chrome Preview */}
        <div className="border-b border-border/70 bg-[#0d0c14]">
          {/* Chrome titlebar */}
          <div className="px-4 py-2.5 border-b border-border/60 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-white/20" />
              <span className="h-2 w-2 rounded-full bg-white/20" />
              <span className="h-2 w-2 rounded-full bg-white/20" />
              <span className="ml-2 font-mono text-[10px] text-muted-foreground/80">
                {previewHeader}
              </span>
            </div>
            <span className={`font-mono text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full border ${statusColor}`}>
              {statusBadge}
            </span>
          </div>

          {/* Canvas area customized per project */}
          <div className="p-4 sm:p-5 bg-gradient-to-b from-[#0a0910] to-[#07060c] min-h-[160px] flex flex-col justify-center">
            {name === "MavecodeCourse" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between font-mono text-[10.5px]">
                  <span className="text-foreground/90 font-medium">Curriculum Progression</span>
                  <span className="text-emerald-400">78% Complete</span>
                </div>
                {/* Progress bar */}
                <div className="w-full h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-violet-500 to-emerald-400 w-[78%]" />
                </div>
                {/* Module checkpoints */}
                <div className="grid grid-cols-3 gap-2 pt-1 font-mono text-[9.5px]">
                  <div className="p-2 rounded border border-border/50 bg-[#12111d] text-center">
                    <span className="text-muted-foreground block">MOD 01</span>
                    <span className="text-emerald-400 font-medium">Passed</span>
                  </div>
                  <div className="p-2 rounded border border-border/50 bg-[#12111d] text-center">
                    <span className="text-muted-foreground block">MOD 02</span>
                    <span className="text-emerald-400 font-medium">Passed</span>
                  </div>
                  <div className="p-2 rounded border border-primary/40 bg-primary/10 text-center">
                    <span className="text-primary block">MOD 03</span>
                    <span className="text-foreground font-medium">Active</span>
                  </div>
                </div>
              </div>
            )}

            {name === "MaveMentor AI" && (
              <div className="space-y-2.5 font-mono text-[10px]">
                <div className="p-2 rounded border border-border/60 bg-[#12101b] flex items-center justify-between">
                  <span className="text-muted-foreground">USER QUERY</span>
                  <span className="text-foreground/90 truncate max-w-[180px]">Explain B-Tree rebalancing</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-primary text-[9px]">
                  <span className="h-px bg-border/80 flex-1" />
                  <span>COSINE EMBEDDING (0.91)</span>
                  <span className="h-px bg-border/80 flex-1" />
                </div>
                <div className="p-2 rounded border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between">
                  <span className="text-emerald-400 font-medium">GROUNDED CONTEXT INJECTED</span>
                  <span className="text-muted-foreground">Gemini API 1.5</span>
                </div>
              </div>
            )}

            {name === "AI Content Curation" && (
              <div className="space-y-2.5 font-mono text-[10px]">
                <div className="flex items-center justify-between text-muted-foreground text-[9.5px]">
                  <span>PRISMA 2020 FLOW PIPELINE</span>
                  <span className="text-primary">SLR INFORMATICS</span>
                </div>
                <div className="grid grid-cols-4 gap-1.5 text-center">
                  <div className="p-1.5 rounded border border-border/50 bg-[#12101b]">
                    <span className="text-[12px] font-medium text-foreground block">1,420</span>
                    <span className="text-[8.5px] text-muted-foreground">Fetched</span>
                  </div>
                  <div className="p-1.5 rounded border border-border/50 bg-[#12101b]">
                    <span className="text-[12px] font-medium text-foreground block">840</span>
                    <span className="text-[8.5px] text-muted-foreground">Dedup</span>
                  </div>
                  <div className="p-1.5 rounded border border-border/50 bg-[#12101b]">
                    <span className="text-[12px] font-medium text-foreground block">210</span>
                    <span className="text-[8.5px] text-muted-foreground">Screen</span>
                  </div>
                  <div className="p-1.5 rounded border border-violet-500/40 bg-violet-500/10">
                    <span className="text-[12px] font-medium text-violet-300 block">48</span>
                    <span className="text-[8.5px] text-violet-400">Synthesized</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground/80">
              Project // {id}
            </span>
            <span className="font-mono text-[10px] tracking-wider uppercase text-primary/90 font-medium">
              {type}
            </span>
          </div>

          <h3 className="text-2xl font-medium tracking-tight text-foreground group-hover:text-primary transition-colors">
            <Link to={`/works/${projectSlug}`} className="focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
              {name}
            </Link>
          </h3>

          <p className="mt-2.5 text-[14px] text-foreground/80 leading-relaxed">
            {desc}
          </p>

          {/* Stack pills */}
          <div className="mt-4 flex flex-wrap gap-1.5">
            {stack.map((t) => (
              <TechTag key={t} label={t} />
            ))}
          </div>
        </div>
      </div>

      {/* Card Actions Footer */}
      <div className="p-6 pt-0 border-t border-border/40 mt-4 flex items-center justify-between">
        <Link
          to={`/works/${projectSlug}`}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.18em] uppercase text-primary hover:underline font-medium focus-visible:ring-2 focus-visible:ring-primary"
        >
          Read Case Study <ArrowRight className="h-3 w-3" />
        </Link>

        <div className="flex items-center gap-2">
          {liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Launch live preview for ${name}`}
              className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-border/80 text-muted-foreground hover:text-primary hover:border-primary/60 hover:bg-secondary/40 transition-colors focus-visible:ring-2 focus-visible:ring-primary"
              title="Launch Live Preview"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          {repoUrl && (
            <a
              href={repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View GitHub repository for ${name}`}
              className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-border/80 text-muted-foreground hover:text-foreground hover:border-border transition-colors focus-visible:ring-2 focus-visible:ring-primary"
              title="View GitHub Repository"
            >
              <Github className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}
